/**
 * Macro runtime state — tracks macro state per macroId, so the play tile
 * can render arm/run/kill visuals.
 *
 * One global signal-map keyed by macroId. Each entry is the latest state
 * snapshot. Called directly by `opbridge/enginesStatePoll.ts` per poll tick.
 */

import { createSignal, type Accessor } from 'solid-js';
import type { MacroStateMessage } from '@opbuttons/schema';

export type MacroRuntimeState = MacroStateMessage['state'];

export interface MacroRuntimeSnapshot {
  state: MacroRuntimeState;
  startBeat: number;
  laneProgress: number[];
  laneValues: number[];
  /** Wall-clock ms when we last received any state for this macro. Used by
   *  the UI to fall back to idle if the bridge goes silent. */
  ts: number;
}

const [states, setStates] = createSignal<Record<string, MacroRuntimeSnapshot>>({});

export const macroStates: Accessor<Record<string, MacroRuntimeSnapshot>> = states;

export function recordMacroState(msg: MacroStateMessage): void {
  const snap: MacroRuntimeSnapshot = {
    state: msg.state,
    startBeat: msg.startBeat,
    laneProgress: msg.laneProgress,
    laneValues: msg.laneValues,
    ts: Date.now(),
  };
  setStates((prev) => ({ ...prev, [msg.macroId]: snap }));
}

/** Best-effort accessor for a specific macro's state. Returns 'idle' when
 *  the bridge has never reported on this macro (or last report was idle). */
export function macroState(macroId: string): MacroRuntimeSnapshot {
  const s = states()[macroId];
  return s ?? { state: 'idle', startBeat: 0, laneProgress: [], laneValues: [], ts: 0 };
}
